import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { cn } from "@/lib/utils"

const Tabs = TabsPrimitive.Root

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, children, ...props }, ref) => {
  const [activeRect, setActiveRect] = React.useState<{
    width: number
    left: number
  } | null>(null)
  const listRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const updateActiveRect = () => {
      if (!listRef.current) return
      const activeTab = listRef.current.querySelector('[data-state="active"]') as HTMLElement
      if (activeTab) {
        const listRect = listRef.current.getBoundingClientRect()
        const tabRect = activeTab.getBoundingClientRect()
        setActiveRect({
          width: tabRect.width,
          left: tabRect.left - listRect.left,
        })
      }
    }

    updateActiveRect()

    // Observer pour détecter les changements d'onglet
    const observer = new MutationObserver(updateActiveRect)
    if (listRef.current) {
      observer.observe(listRef.current, {
        attributes: true,
        subtree: true,
        attributeFilter: ['data-state'],
      })
    }

    window.addEventListener('resize', updateActiveRect)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', updateActiveRect)
    }
  }, [])

  return (
    <TabsPrimitive.List
      ref={(node) => {
        // Handle both refs
        if (typeof ref === 'function') ref(node)
        else if (ref) ref.current = node
        ;(listRef as React.MutableRefObject<HTMLDivElement | null>).current = node
      }}
      className={cn(
        "relative inline-flex items-center gap-1 rounded-full bg-slate-100 p-1 dark:bg-slate-800",
        className
      )}
      {...props}
    >
      {/* Animated background indicator */}
      {activeRect && (
        <div
          className="absolute top-1 bottom-1 rounded-full bg-white transition-all duration-300 ease-out dark:bg-slate-700"
          style={{
            width: activeRect.width,
            left: activeRect.left,
          }}
        />
      )}
      {children}
    </TabsPrimitive.List>
  )
})
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "relative z-10 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200",
      "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100",
      "focus-visible:outline-none",
      "disabled:pointer-events-none disabled:opacity-50",
      "data-[state=active]:text-blue-600 dark:data-[state=active]:text-blue-400",
      className
    )}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-4 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      "data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-bottom-2 data-[state=inactive]:animate-out data-[state=inactive]:fade-out-0",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
